import { Injectable } from '@angular/core';
import { GoogleAddress } from '../models/GoogleAddress.model';
import { EnderecoFiltro, ResolveErrorSource } from '../models/EnderecoFiltro.model';
import { Subject } from 'rxjs';
declare const google: any;

@Injectable()
export class AutoCompleteGoogleService {
  private google;
  private autoComplete;
  googleAddressResponse = new Subject<GoogleAddress>();
  private enderecoFiltroResponse = new Subject<EnderecoFiltro>();
  private resolveErrorResponse = new Subject<ResolveErrorSource>();
  SERVICO_TIPO_ENUM;

  listenResponseGoogle(){
    return this.googleAddressResponse.asObservable();
  }

  listenEnderecoFiltro() {
    return this.enderecoFiltroResponse.asObservable();
  }

  listenResolveError() {
    return this.resolveErrorResponse.asObservable();
  }

  searchLocation(searchInput: HTMLElement) {
    this.google = google;

    this.autoComplete = new this.google.maps.places.Autocomplete(searchInput, {
      types: ['geocode'],
    });

    this.autoComplete.setFields(['address_component']);

    google.maps.event.addListener(this.autoComplete, 'place_changed', () => {
      const places = this.autoComplete.getPlace();
      const addressResponse = this.parseAddressComponents(places.address_components);
      const geocoder = new google.maps.Geocoder();
      const address = `${addressResponse.rua}, ${addressResponse.numero} - ${addressResponse.bairro} - ${addressResponse.cidade} - ${addressResponse.uf} - Brasil - ${addressResponse.cep}`;

      geocoder.geocode({ address }, (results, status) => {
        if (status == google.maps.GeocoderStatus.OK) {
          addressResponse.latitude = results[0].geometry.location.lat().toFixed(7).toString();
          addressResponse.longitude = results[0].geometry.location.lng().toFixed(7).toString();
          const googleAddress = this.getFullAddress(addressResponse);
          this.googleAddressResponse.next(googleAddress);
        }
      });
    });
  }

  initAutocomplete(inputElement: HTMLElement, options: { types?: string[] } = {}): void {
    this.google = google;

    const autocomplete = new this.google.maps.places.Autocomplete(inputElement, {
      types: options.types || ['geocode'],
    });

    autocomplete.setFields(['address_component']);

    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const places = autocomplete.getPlace();
      const enderecoFiltro: EnderecoFiltro = {};

      this.parseGeocoderAddressComponents(places?.address_components, enderecoFiltro);

      const completeAddress = places?.address_components?.map((x) => x.long_name).join(', ');
      const geocoder = new google.maps.Geocoder();

      geocoder.geocode({ address: completeAddress }, (results, status) => {
        if (status == google.maps.GeocoderStatus.OK && results?.length > 0) {
          this.handleAddress(results, enderecoFiltro);
          this.enderecoFiltroResponse.next({ ...enderecoFiltro });
        }
      });
    });
  }

  getAddressByLatLng(latitude: number, longitude: number) {
    const latlng = { lat: latitude, lng: longitude };
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({location: latlng}, (results, status) => {
      if (status == google.maps.GeocoderStatus.OK) {
        const places = results[0];
        const fullAddress = results[0].formatted_address;

        const googleAddress = this.parseAddressComponents(places.address_components);
        googleAddress.latitude = latitude.toString();
        googleAddress.longitude = longitude.toString();
        googleAddress.fullAddress = fullAddress;

        this.googleAddressResponse.next(googleAddress);
      }
    });
  }

  resolveByLatLng(lat: number, lng: number): void {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results?.length > 0) {
        const enderecoFiltro: EnderecoFiltro = {};
        this.parseGeocoderAddressComponents(results[0].address_components, enderecoFiltro);
        enderecoFiltro.lat = lat.toFixed(7).toString();
        enderecoFiltro.lng = lng.toFixed(7).toString();
        enderecoFiltro.formattedAddress = results[0].formatted_address;
        this.enderecoFiltroResponse.next({ ...enderecoFiltro });
      } else {
        this.resolveErrorResponse.next('latLng');
      }
    });
  }

  resolveByPlusCode(plusCode: string): void {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: plusCode }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results?.length > 0) {
        const enderecoFiltro: EnderecoFiltro = {};
        this.parseGeocoderAddressComponents(results[0].address_components, enderecoFiltro);
        this.handleAddress(results, enderecoFiltro);
        enderecoFiltro.formattedAddress = results[0].formatted_address;
        this.enderecoFiltroResponse.next({ ...enderecoFiltro });
      } else {
        this.resolveErrorResponse.next('plusCode');
      }
    });
  }

  parseLatLngInput(value: string): { lat: number; lng: number } | null {
    const latLngRegex = /^([-+]?\d+\.?\d*)\s*[,;\s]\s*([-+]?\d+\.?\d*)$/;
    const match = value.match(latLngRegex);
    if (!match) return null;

    const lat = parseFloat(match[1]);
    const lng = parseFloat(match[2]);
    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng };
    }
    return null;
  }

  isPlusCodeInput(value: string): boolean {
    return /^[23456789CFGHJMPQRVWX]+\+[23456789CFGHJMPQRVWX]+/i.test(value);
  }

  parseGeocoderAddressComponents(
    addressComponents: any[] = [],
    enderecoFiltro: EnderecoFiltro
  ): EnderecoFiltro {
    return addressComponents.reduce((acc, component) => {
      const typeMap: Record<string, keyof EnderecoFiltro> = {
        postal_code: 'cep',
        street_number: 'numero',
        route: 'rua',
        plus_code: 'rua',
        sublocality_level_1: 'bairro',
        sublocality: 'bairro',
        administrative_area_level_2: 'cidade',
        locality: 'cidade',
      };

      for (const type of component.types) {
        if (type === 'administrative_area_level_1') {
          acc.uf = component.short_name;
          break;
        }

        const field = typeMap[type];
        if (field) {
          acc[field] = component.long_name;
          break;
        }
      }

      return acc;
    }, enderecoFiltro);
  }

  handleAddress(results: Array<any>, enderecoFiltro: EnderecoFiltro): void {
    if (!results || results.length === 0) { return; }
    const googleAddress = results.reduce((prev, current) =>
      (current.address_components?.length > prev.address_components?.length ? current : prev), results[0]
    );
    const { location } = googleAddress.geometry;
    enderecoFiltro.lat = location.lat().toFixed(7).toString();
    enderecoFiltro.lng = location.lng().toFixed(7).toString();
  }

  private parseAddressComponents(components: Array<any>) {
    const addressResponse = new GoogleAddress();

    components.forEach(x => {
      switch (x.types[0]) {
        case 'postal_code':
          addressResponse.cep = x.short_name;
          break;
        case 'street_number':
          addressResponse.numero = x.long_name;
          break;
        case 'route':
          addressResponse.rua = x.long_name;
          break;
        case 'sublocality_level_1':
          addressResponse.bairro = x.long_name;
          break;
        case 'administrative_area_level_2':
          addressResponse.cidade = x.long_name;
          break;
        case 'administrative_area_level_1':
          addressResponse.uf = x.short_name;
          break;
      }
    });

    return addressResponse;
  }

  private getFullAddress(response: GoogleAddress): GoogleAddress {
    const numero = response.numero ? `, ${response.numero}` : '';
    response.fullAddress = `${response.rua}${numero} - ${response.bairro}, ${response.cidade} - ${response.uf}`;
    return response;
  }
}
